import { Input, InputGroup, Select } from '..';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';

import {
  ADDRESS_FIELDS,
  ADDRESS_STRUCTURE,
  POSTAL_CODE_FORMATS,
  VALIDATION_MESSAGES,
} from '../../../constants/address-structure';
import { countries } from '../../../constants/countries';
import {
  COUNTRIES_WITH_REQUIRED_COUNTY,
  COUNTRIES_WITH_REQUIRED_STATE,
  COUNTRIES_WITH_STATE_DROPDOWN,
  getFullCountryName,
  getStateOptionValue,
  getStateOptions,
  resolveRegionStateFromPlace,
} from '../../../helpers/countries';
import DisplayBox from '../../DisplayBox/DisplayBox';
import { GoogleAddressInput } from '../GoogleAddressInput/GoogleAddressInput';
import styles from './Address.module.scss';

const ADDRESS_WATCH_FIELDS = [
  ...ADDRESS_FIELDS.map(field => field.fieldName),
  'google_address',
  'manual_address_entry',
];

export const Address = ({
  name,
  value = {},
  title,
  description,
  tooltip,
  autoComplete,
  defaultValue,
  required = false,
  register,
  setValue,
  control,
  error,
  errors,
  validators = {},
  noErrorMessage = false,
  isVisible = true,
  dataTestId,
}) => {
  const [errorMessage, setErrorMessage] = useState(error);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const addressSnapshotRef = useRef('');
  const completeAddressValueRef = useRef({});

  const watchedFieldPaths = useMemo(
    () => ADDRESS_WATCH_FIELDS.map(fieldName => `${name}.${fieldName}`),
    [name],
  );

  const watchedFieldValues = useWatch({
    control,
    name: watchedFieldPaths,
  });

  const completeAddressValue = useMemo(() => {
    const address = {};
    ADDRESS_WATCH_FIELDS.forEach((fieldName, index) => {
      const fieldValue = Array.isArray(watchedFieldValues) ? watchedFieldValues[index] : undefined;
      if (fieldValue !== undefined && fieldValue !== null) {
        address[fieldName] = fieldValue;
      }
    });
    return address;
  }, [watchedFieldValues]);

  completeAddressValueRef.current = completeAddressValue;

  useEffect(() => {
    if (!register || !validators?.validate) return;

    register(name, {
      validate: Object.fromEntries(
        Object.entries(validators.validate).map(([key, validateFn]) => [
          key,
          () => validateFn(completeAddressValueRef.current),
        ]),
      ),
    });
  }, [register, name, validators]);

  const countryValue = completeAddressValue.country;
  const manualAddressEntry = completeAddressValue.manual_address_entry;

  const setAddressField = useCallback(
    (fieldName, fieldValue, options = {}) => {
      setValue(`${name}.${fieldName}`, fieldValue, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: false,
        ...options,
      });
    },
    [name, setValue],
  );

  const isManualMode = isManualEntry || manualAddressEntry === true;

  const selectedCountry =
    countryValue || completeAddressValue?.country || getFullCountryName(defaultValue);
  const structure = ADDRESS_STRUCTURE[selectedCountry] || ADDRESS_STRUCTURE.default;

  const prevCountryRef = useRef(getFullCountryName(selectedCountry));

  useEffect(() => {
    addressSnapshotRef.current = '';
    prevCountryRef.current = getFullCountryName(completeAddressValue.country || defaultValue);
  }, [name]);

  const googleFieldValidators = !isVisible
    ? {}
    : {
        required: {
          value: required && !isManualMode,
          message: 'Address is required',
        },
      };

  useEffect(() => {
    if (!error) {
      setErrorMessage(null);
      return;
    }

    if (typeof error === 'string') {
      setErrorMessage(error);
      return;
    }

    if (typeof error !== 'object' || Object.keys(error).length === 0) {
      setErrorMessage(null);
      return;
    }

    // Get all errors
    const allErrors = Object.values(error);

    // First check for required errors
    const requiredErrors = allErrors.filter(err => err?.type === 'required');
    if (requiredErrors.length > 0) {
      setErrorMessage(
        requiredErrors.length > 1
          ? 'Please complete all required address fields'
          : requiredErrors[0].message,
      );
      return;
    }

    // If no required errors, check for other error types
    const otherErrors = allErrors.filter(err => err?.message);
    if (otherErrors.length > 0) {
      setErrorMessage(
        otherErrors.length > 1
          ? 'Please fix the errors in the address fields'
          : otherErrors[0].message,
      );
      return;
    }

    setErrorMessage(null);
  }, [error]);

  useEffect(() => {
    if (manualAddressEntry !== undefined) {
      setIsManualEntry(manualAddressEntry);
    }
  }, [manualAddressEntry]);

  useEffect(() => {
    if (!isManualMode || !defaultValue) return;
    const formattedCountry = getFullCountryName(defaultValue);
    if (!formattedCountry || countryValue) return;
    setAddressField('country', formattedCountry);
  }, [isManualMode, countryValue, defaultValue, setAddressField]);

  useEffect(() => {
    const snapshot = JSON.stringify(completeAddressValue);
    if (snapshot === addressSnapshotRef.current) return;
    addressSnapshotRef.current = snapshot;

    const hasValues = ADDRESS_FIELDS.some(field => completeAddressValue[field.fieldName]);

    if (hasValues) {
      if ((manualAddressEntry === undefined || manualAddressEntry === null) && !isManualEntry) {
        const shouldUseManual = !completeAddressValue.google_address;
        setIsManualEntry(shouldUseManual);
        setAddressField('manual_address_entry', shouldUseManual);
      }

      // If there's a country value, ensure it's in the correct format
      const currentCountry = countryValue || completeAddressValue?.country;
      if (currentCountry) {
        const formattedCountry = getFullCountryName(currentCountry);
        if (formattedCountry !== currentCountry) {
          setAddressField('country', formattedCountry, { shouldValidate: true });
        }
        // When country actually changes (not initial load), clear state/county and fields not in new country's structure
        const effectiveCountry = getFullCountryName(currentCountry);
        if (effectiveCountry != null && effectiveCountry !== prevCountryRef.current) {
          if (prevCountryRef.current == null) {
            prevCountryRef.current = effectiveCountry;
          } else {
            prevCountryRef.current = effectiveCountry;
            const newStructure = ADDRESS_STRUCTURE[effectiveCountry] || ADDRESS_STRUCTURE.default;
            const updatedAddress = { ...completeAddressValue };
            let hasChanges = false;
            const clearOnCountryChange = ['region_state', 'administrative_area_level_2'];
            for (let i = 0; i < ADDRESS_FIELDS.length; i++) {
              const fieldName = ADDRESS_FIELDS[i].fieldName;
              if (fieldName === 'country') continue;
              const inNewStructure = newStructure[fieldName];
              const alwaysClear = clearOnCountryChange.includes(fieldName);
              if (inNewStructure && !alwaysClear) continue;
              if (updatedAddress[fieldName]) {
                updatedAddress[fieldName] = '';
                hasChanges = true;
              }
            }
            if (hasChanges) {
              const setOptions = { shouldValidate: !isManualMode };
              for (let i = 0; i < ADDRESS_FIELDS.length; i++) {
                const fieldName = ADDRESS_FIELDS[i].fieldName;
                if (updatedAddress[fieldName] !== completeAddressValue[fieldName]) {
                  setAddressField(fieldName, updatedAddress[fieldName] ?? '', setOptions);
                }
              }
            }
          }
        }
      } else if (defaultValue && !isManualMode) {
        const formattedCountry = getFullCountryName(defaultValue);
        setAddressField('country', formattedCountry, { shouldValidate: true });
      }
    } else if (!isManualMode) {
      prevCountryRef.current = null;
    }
  }, [
    completeAddressValue,
    countryValue,
    setAddressField,
    defaultValue,
    manualAddressEntry,
    isManualEntry,
    isManualMode,
  ]);

  const getFieldValidators = fieldName => {
    if (!isVisible) {
      return {};
    }

    const fieldValidators = {};

    // Add required validation based on field and country
    if (structure[fieldName]) {
      let isRequired = false;

      if (fieldName === 'suburb') {
        isRequired = false;
      } else if (fieldName === 'region_state') {
        isRequired = required && COUNTRIES_WITH_REQUIRED_STATE.includes(selectedCountry);
      } else if (fieldName === 'administrative_area_level_2') {
        isRequired = required && COUNTRIES_WITH_REQUIRED_COUNTY.includes(selectedCountry);
      } else {
        isRequired = required;
      }

      if (isRequired) {
        fieldValidators.required = {
          value: true,
          message: `${structure[fieldName]} is required`,
        };
      }
    }

    // Add postal code validation if applicable
    if (fieldName === 'postal_code' && structure.postal_code) {
      const postalFormat = POSTAL_CODE_FORMATS[selectedCountry] || POSTAL_CODE_FORMATS.default;
      fieldValidators.pattern = {
        value: new RegExp(postalFormat),
        message:
          VALIDATION_MESSAGES.postal_code[selectedCountry] ||
          VALIDATION_MESSAGES.postal_code.default,
      };
    }

    return fieldValidators;
  };

  const handleManualEntry = e => {
    const isManual = e.target.checked;
    const activeElement = document.activeElement;

    if (isManual) {
      setIsManualEntry(true);
      setAddressField('manual_address_entry', true);
      setAddressField('google_address', '');
      clearAddress({ manualEntry: true });
    } else {
      setIsManualEntry(false);
      setAddressField('manual_address_entry', false);
    }

    setErrorMessage(null);

    requestAnimationFrame(() => {
      if (activeElement instanceof HTMLElement && document.contains(activeElement)) {
        activeElement.focus({ preventScroll: true });
      }
    });
  };

  const handleAddressSelect = placeDetails => {
    const findComponent = componentTypes => {
      // Special handling for street_address to combine multiple components
      if (componentTypes.includes('street_number')) {
        const subpremise =
          placeDetails?.addressComponents?.find(component => component.types.includes('subpremise'))
            ?.longText || '';

        const streetNumber =
          placeDetails?.addressComponents?.find(component =>
            component.types.includes('street_number'),
          )?.longText || '';

        const route =
          placeDetails?.addressComponents?.find(component => component.types.includes('route'))
            ?.longText || '';

        const baseStreet = [streetNumber, route].filter(Boolean).join(' ');

        if (subpremise) {
          return `${subpremise}/${baseStreet}`;
        }

        // GetPlace strips sub-premises (e.g. "127a" → "127"). Extract the street
        // portion directly from the autocomplete formattedAddress which preserves them.
        if (placeDetails?.formattedAddress && route) {
          const routeIndex = placeDetails.formattedAddress.indexOf(route);
          if (routeIndex > 0) {
            const streetPart = placeDetails.formattedAddress
              .substring(0, routeIndex + route.length)
              .trim();
            if (streetPart !== baseStreet) {
              return streetPart;
            }
          }
        }

        return baseStreet;
      }

      // Handle other fields normally
      for (const type of componentTypes) {
        const component = placeDetails?.addressComponents?.find(component =>
          component.types.includes(type),
        );
        if (component?.longText) {
          return component.longText;
        }
        if (component?.shortText) {
          return component.shortText;
        }
      }
      return '';
    };

    // Build a complete new address object to replace the entire nested object
    // This avoids the "object is not extensible" error
    const newAddressObject = { ...completeAddressValue };

    // Clear all existing address fields first
    ADDRESS_FIELDS.forEach(field => {
      newAddressObject[field.fieldName] = '';
    });

    // Update each address field using the googleComponents mapping from ADDRESS_FIELDS
    ADDRESS_FIELDS.forEach(field => {
      if (field.googleComponents) {
        const fieldValue = findComponent(field.googleComponents);
        newAddressObject[field.fieldName] = fieldValue;
      } else {
        newAddressObject[field.fieldName] = '';
      }
    });

    // Handle country separately since it needs special formatting
    const countryAlpha2 =
      placeDetails?.addressComponents?.find(c => c.types.includes('country'))?.shortText ||
      findComponent(['country']); // fallback if no shortText

    const formattedCountry = getFullCountryName(countryAlpha2);
    if (formattedCountry) {
      newAddressObject.country = formattedCountry;
    } else if (defaultValue) {
      // Set default country if no country found
      newAddressObject.country = getFullCountryName(defaultValue);
    }

    // Set google_address and manual_address_entry
    newAddressObject.google_address = placeDetails?.formattedAddress || '';
    newAddressObject.manual_address_entry = false;

    newAddressObject.region_state = resolveRegionStateFromPlace({
      formattedAddress: newAddressObject.google_address,
      countryValue: newAddressObject.country,
      regionState: newAddressObject.region_state,
    });

    Object.entries(newAddressObject).forEach(([fieldName, fieldValue]) => {
      setAddressField(fieldName, fieldValue ?? '', { shouldValidate: true });
    });

    setIsManualEntry(false);
  };

  const clearAddress = ({ manualEntry = false } = {}) => {
    const formattedCountry = getFullCountryName(defaultValue);
    const setOptions = { shouldValidate: !manualEntry };

    ADDRESS_FIELDS.forEach(field => {
      setAddressField(field.fieldName, '', setOptions);
    });

    if (manualEntry) {
      setAddressField('manual_address_entry', true, setOptions);
      setAddressField('google_address', '', setOptions);
      if (formattedCountry) {
        setAddressField('country', formattedCountry, setOptions);
      }
    } else if (formattedCountry) {
      setAddressField('country', formattedCountry, setOptions);
    }
  };

  const hasAddressValues = () => {
    return isManualMode || completeAddressValue.google_address;
  };

  return (
    <InputGroup title={title} description={description} tooltip={tooltip} error={errorMessage}>
      <div
        className={cn(styles.addressContainer, {
          [styles.addressContainerError]: errorMessage,
        })}>
        <GoogleAddressInput
          name={`${name}.google_address`}
          parentName={name}
          register={register}
          setValue={setValue}
          onManualEntry={handleManualEntry}
          onAddressSelect={handleAddressSelect}
          country={defaultValue}
          hasManualEntry={isManualMode}
          validators={googleFieldValidators}
          dataTestId={dataTestId}
        />

        {isManualMode && (
          <>
            <div className={styles.manualFields}>
              {ADDRESS_FIELDS.map(field => {
                const isFieldInStructure =
                  structure[field.fieldName] && structure[field.fieldName] !== null;

                if (!isFieldInStructure) return null;

                if (
                  field.fieldName === 'region_state' &&
                  COUNTRIES_WITH_STATE_DROPDOWN.includes(selectedCountry)
                ) {
                  const stateOptions = getStateOptions(selectedCountry);
                  return (
                    <Select
                      key={field.fieldName}
                      name={`${name}.${field.fieldName}`}
                      value={getStateOptionValue(
                        completeAddressValue?.region_state,
                        selectedCountry,
                      )}
                      setValue={setValue}
                      placeholder={`Select ${structure[field.fieldName]}...`}
                      validators={isManualMode ? getFieldValidators(field.fieldName) : {}}
                      register={register}
                      error={errors?.[name]?.[field.fieldName]?.message}
                      noErrorMessage={true}
                      className="u-margin-bottom--small">
                      {stateOptions.map(state => (
                        <Select.Item key={state.value} value={state.value}>
                          {state.name}
                        </Select.Item>
                      ))}
                    </Select>
                  );
                }

                return (
                  <Input
                    key={field.fieldName}
                    name={`${name}.${field.fieldName}`}
                    type="text"
                    autoComplete={autoComplete === 'current' ? field.autocomplete : undefined}
                    placeholder={structure[field.fieldName]}
                    maxLength={field.maxLength}
                    validators={isManualMode ? getFieldValidators(field.fieldName) : {}}
                    register={register}
                    error={errors?.[name]?.[field.fieldName]?.message}
                    noErrorMessage={true}
                    className="u-margin-bottom--small"
                  />
                );
              })}
              <Select
                name={`${name}.country`}
                value={countryValue}
                setValue={setValue}
                placeholder="Select Country..."
                useSearch
                validators={
                  required && isManualMode && isVisible ? { required: 'Country is required' } : {}
                }
                register={register}
                error={errors?.[name]?.country?.message ?? error?.country?.message}
                noErrorMessage={true}>
                {countries.map(country => (
                  <Select.Item key={country.value} value={country.value}>
                    {country.name}
                  </Select.Item>
                ))}
              </Select>
            </div>
          </>
        )}
        <DisplayBox show={!hasAddressValues()}>
          <div className="d-flex flex-column u-align-items-center">
            <span className="primary-text title-5 text-center">No address selected</span>
            <span className="secondary-text t-body text-center">
              Search for, or enter an address manually
            </span>
          </div>
        </DisplayBox>
      </div>
      {errorMessage && !noErrorMessage && <span className={styles.error}>{errorMessage}</span>}
    </InputGroup>
  );
};

Address.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.object,
  title: PropTypes.string,
  description: PropTypes.string,
  tooltip: PropTypes.string,
  autoComplete: PropTypes.string,
  defaultValue: PropTypes.string,
  required: PropTypes.bool,
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  control: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  errors: PropTypes.object,
  validators: PropTypes.object,
  noErrorMessage: PropTypes.bool,
  isVisible: PropTypes.bool,
  dataTestId: PropTypes.string,
};
